import type { PrWorkspace, PrDraft, PreparePrResponse, PrOperation, PrGitHub, SavePrDraftRequest, PreparePrRequest, RunPrActionRequest, UpdatePrMetadataRequest } from "./pr-workspace.ts";
import type { z } from "zod";
import type {
  AcceptInviteRequest,
  ApiTokensResponse,
  AssistantStateResponse,
  AccountLinkPreviewResponse,
  RedeemAccountLinkRequest,
  RedeemAccountLinkResponse,
  ChannelIdentityLinkResponse,
  ChannelInstallationsResponse,
  CreateApiTokenRequest,
  CreateApiTokenResponse,
  StartAssistantConversationRequest,
  StartAssistantConversationResponse,
  StartChannelInstallRequest,
  StartChannelInstallResponse,
  CancelChatRequest,
  ChatDetailResponse,
  ChatEventsPage,
  CheckoutRequest,
  CheckoutResponse,
  ClaimResponse,
  CreateChatRequest,
  HandoffChatRequest,
  UpdateChatRequest,
  UpdateChatResponse,
  HandoffChatResponse,
  MergePullRequestRequest,
  MergePullRequestResponse,
  PullRequestStatus,
  CreateInviteRequest,
  CreateInviteResponse,
  CreateOrgRequest,
  CreateRepoRequest,
  CreateTaskRequest,
  CreateTaskResponse,
  FileContentResponse,
  GitCredentialsResponse,
  GitHubReposResponse,
  GitHubStatusResponse,
  IntegrationStatusResponse,
  KeyboardShortcutPreferencesResponse,
  UpdateIntegrationRequest,
  UpdateKeyboardShortcutPreferencesRequest,
  MeResponse,
  NativeDevicesResponse,
  AuthSessionsResponse,
  RevokeOtherSessionsResponse,
  OrgDetailResponse,
  RegisterWorkerRequest,
  RegisterWorkerResponse,
  RegisterDeviceRequest,
  RecordHandoffBackupRequest,
  RepoBranchesResponse,
  RepoPullRequestsResponse,
  LinearIssuesResponse,
  SendMessageRequest,
  ReviseMessageRequest,
  UpdateUserPreferencesRequest,
  UploadAttachmentResponse,
  UserPreferencesResponse,
  UploadSnapshotRequest,
  WorktreeDetailResponse,
  SnapshotDiffResponse,
  SnapshotDiffScope,
  SnapshotPathsResponse,
} from "./api.ts";
import { SNAPSHOT_SUMMARY_VIEW } from "./api.ts";
import { formatChatEventsQuery, CHAT_EVENTS_DEFAULT_LIMIT, type ChatEventsQuery } from "./chat-history.ts";
import type { LocalSessionTicketRequest, LocalSessionTicketResponse } from "./local-api.ts";
import type {
  AttachmentMediaType,
  AttachmentRef,
  Chat,
  Job,
  Org,
  Repo,
  SnapshotSummary,
  WorkerInfo,
  Worktree,
} from "./models.ts";
import { withProtocolVersion } from "./protocol-version.ts";
import { WORKER_PROOF_HEADER, WorkerProofErrorCode, type WorkerProofProvider, type WorkerPublicJwk } from "./worker-proof.ts";
import type { DirectTicketRequest, DirectTicketResponse } from "./transport-descriptor.ts";
import type { PromptKind, PromptSettingsResponse, UpdatePromptRequest } from "./prompts.ts";

export class RivetsApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = "RivetsApiError";
  }
}

/** Default deadline for a JSON call. Past it the request is aborted and fails
 * with `request_timeout`, so nothing waiting on it can spin forever. */
export const REQUEST_TIMEOUT_MS = 30_000;
/** Default deadline for a call that moves file bytes: attachments, snapshot
 * uploads and diffs, identity images. */
export const TRANSFER_TIMEOUT_MS = 120_000;
/** Deadline for a call the server answers only after a worker round trip
 * (git, PR, file save, discard). WorkerHub waits up to two minutes for the
 * worker, so the client must outwait it, or it gives up on an operation the
 * server is still running and usually completes. Matches native. */
export const WORKER_ACTION_TIMEOUT_MS = 150_000;
/** Deadline for starting or resetting the assistant, which provisions its
 * workspace before answering. Matches native. */
export const ASSISTANT_TIMEOUT_MS = 90_000;
/** `RivetsApiError.code` for a request that hit its deadline. Nothing in this
 * client retries one: on a mutation it means the outcome is unknown, and the
 * caller must check rather than send it again. */
export const REQUEST_TIMEOUT_CODE = "request_timeout";

export interface RivetsClientOptions {
  baseUrl: string;
  /** Bearer token (CLI / native). Web uses cookie sessions and omits this. */
  token?: string;
  /** Worker-only proof signer. User bearer clients leave this unset. */
  workerProof?: WorkerProofProvider;
  fetch?: typeof fetch;
  /** Overrides `REQUEST_TIMEOUT_MS`. */
  requestTimeoutMs?: number;
  /** Overrides `TRANSFER_TIMEOUT_MS`. */
  transferTimeoutMs?: number;
}

/** A request's deadline: the JSON default, the transfer default, or an
 * explicit one in milliseconds. */
type Deadline = "json" | "transfer" | number;

interface SendOptions {
  headers?: Record<string, string>;
  body?: BodyInit;
  signal?: AbortSignal;
  deadline?: Deadline;
}

/**
 * Typed REST client shared by web, CLI and (later) native apps.
 * Cookie mode: pass no token and Requests include credentials.
 */
export class RivetsClient {
  /** Applied to every request this instance makes; see `withSignal`. */
  private boundSignal?: AbortSignal;
  /** How far the server's clock is ahead of this machine's, learned from a
   * `worker_proof_stale` answer. Proofs are stamped on the server's clock, so
   * a drifting machine stops failing every request. Shared with `withSignal`
   * copies, which are the same client. */
  private proofClock = { offsetMs: 0 };

  constructor(private opts: RivetsClientOptions) {}

  /**
   * The same client with every request bound to `signal`. React Query hands a
   * query function the signal it aborts when a query is cancelled or its
   * result is no longer wanted, so `queryFn: ({ signal }) =>
   * client.withSignal(signal).worktreeDetail(id)` stops a superseded read from
   * downloading, rather than only ignoring its answer. Cheap: nothing but the
   * options object is shared.
   */
  withSignal(signal: AbortSignal | undefined): RivetsClient {
    if (!signal) return this;
    const bound = new RivetsClient(this.opts);
    bound.boundSignal = signal;
    bound.proofClock = this.proofClock;
    return bound;
  }

  private req<T>(
    method: string,
    path: string,
    body?: unknown,
    explicitSignal?: AbortSignal,
    deadline: Deadline = "json",
  ): Promise<T> {
    return this.send(method, `${this.opts.baseUrl}${path}`, {
      headers: body !== undefined ? { "content-type": "application/json" } : {},
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: explicitSignal,
      deadline,
    }, async (res) => {
      if (res.status === 204) return undefined as T;
      return (await res.json()) as T;
    });
  }

  /**
   * Every request goes through here. It applies the deadline (combined with
   * the caller's signal, which still cancels without being reported as a
   * timeout) across the whole exchange, body included, signs a worker proof
   * per attempt, and re-signs once when the server says the proof was stale
   * or replayed: a request that sat out a machine's sleep is signed again
   * rather than failed.
   */
  private async send<T>(method: string, url: string, init: SendOptions, read: (res: Response) => Promise<T>): Promise<T> {
    const f = this.opts.fetch ?? fetch;
    const caller = init.signal ?? this.boundSignal;
    const deadline = AbortSignal.timeout(this.deadlineMs(init.deadline ?? "json"));
    const signal = caller ? AbortSignal.any([caller, deadline]) : deadline;
    try {
      for (let attempt = 0; ; attempt += 1) {
        const res = await f(url, {
          method,
          headers: { ...init.headers, ...await this.authorizationHeaders(method, url) },
          body: init.body,
          signal,
          // Fetch defaults to `same-origin`: browser sessions include their cookie
          // for Rivets API calls while token-authenticated cross-origin CLI calls
          // do not leak browser credentials. Keeping the default also compiles
          // against Cloudflare's narrower RequestInit type.
        });
        if (attempt === 0 && res.status === 401 && this.opts.workerProof && await this.proofWantsResign(res)) continue;
        if (!res.ok) throw await this.failure(res);
        return await read(res);
      }
    } catch (error) {
      if (deadline.aborted && !caller?.aborted) {
        throw new RivetsApiError(0, "Request timed out", REQUEST_TIMEOUT_CODE);
      }
      throw error;
    }
  }

  private deadlineMs(deadline: Deadline): number {
    if (typeof deadline === "number") return deadline;
    return deadline === "transfer"
      ? this.opts.transferTimeoutMs ?? TRANSFER_TIMEOUT_MS
      : this.opts.requestTimeoutMs ?? REQUEST_TIMEOUT_MS;
  }

  /** Whether a 401 refused only the proof's freshness, so the same request
   * signed again would pass. Records the server's clock as it goes. */
  private async proofWantsResign(res: Response): Promise<boolean> {
    try {
      const body = (await res.clone().json()) as { code?: string; serverTime?: unknown };
      if (body.code === WorkerProofErrorCode.stale) {
        if (typeof body.serverTime === "number" && Number.isFinite(body.serverTime)) {
          this.proofClock.offsetMs = body.serverTime - Date.now();
        }
        return true;
      }
      return body.code === WorkerProofErrorCode.replayed;
    } catch {
      return false;
    }
  }

  private async authorizationHeaders(method: string, url: string): Promise<Record<string, string>> {
    if (!this.opts.token) return {};
    const headers: Record<string, string> = { authorization: `Bearer ${this.opts.token}` };
    if (this.opts.workerProof) {
      const offset = this.proofClock.offsetMs;
      headers[WORKER_PROOF_HEADER] = await this.opts.workerProof.createProof({
        method,
        url,
        accessToken: this.opts.token,
        ...(offset !== 0 ? { now: Date.now() + offset } : {}),
      });
    }
    return headers;
  }

  /** The error an API answer carries, for `req()` and the binary paths. */
  private async failure(res: Response): Promise<RivetsApiError> {
    let message = res.statusText;
    let code: string | undefined;
    try {
      // Routes answer `{error, code}`; some (snapshot expiry, LocalApi-shaped
      // errors) answer `{code, message}`. Either wording is the message.
      const data = (await res.json()) as { error?: string; message?: string; code?: string };
      if (data.error) message = data.error;
      else if (data.message) message = data.message;
      code = data.code;
    } catch {
      /* non-JSON error body */
    }
    return new RivetsApiError(res.status, message, code);
  }

  // billing
  checkout(body: z.infer<typeof CheckoutRequest>) {
    return this.req<z.infer<typeof CheckoutResponse>>("POST", "/api/billing/checkout", body);
  }
  claim(sessionId: string, client?: "native") {
    const native = client ? "&client=native" : "";
    return this.req<z.infer<typeof ClaimResponse>>(
      "GET",
      `/api/billing/claim?session_id=${encodeURIComponent(sessionId)}${native}`,
    );
  }

  /** Upload/reset a personal picture or an owner-managed organization icon. */
  async updateIdentityImage(file: Blob | null, orgId?: string): Promise<{ image: string | null }> {
    const path = orgId ? `/api/orgs/${encodeURIComponent(orgId)}/image` : "/api/me/image";
    if (!file) return this.req("DELETE", path);
    return this.send("PUT", `${this.opts.baseUrl}${path}`, {
      headers: { "content-type": file.type },
      body: file,
      deadline: "transfer",
    }, (res) => res.json() as Promise<{ image: string | null }>);
  }

  // me / orgs
  me() {
    return this.req<z.infer<typeof MeResponse>>("GET", "/api/me");
  }
  /** Account-level agent defaults. Server-stored, so every device this account
   * signs in from reads and writes the same values. */
  userPreferences() {
    return this.req<z.infer<typeof UserPreferencesResponse>>("GET", "/api/me/preferences");
  }
  updateUserPreferences(body: z.infer<typeof UpdateUserPreferencesRequest>) {
    return this.req<z.infer<typeof UserPreferencesResponse>>("PUT", "/api/me/preferences", body);
  }
  keyboardShortcutPreferences() {
    return this.req<z.infer<typeof KeyboardShortcutPreferencesResponse>>("GET", "/api/me/keyboard-shortcuts");
  }
  updateKeyboardShortcutPreferences(body: z.infer<typeof UpdateKeyboardShortcutPreferencesRequest>) {
    return this.req<z.infer<typeof KeyboardShortcutPreferencesResponse>>("PUT", "/api/me/keyboard-shortcuts", body);
  }
  promptSettings(orgId: string, repoId?: string) {
    const query = repoId ? `?repoId=${encodeURIComponent(repoId)}` : "";
    return this.req<PromptSettingsResponse>("GET", `/api/orgs/${orgId}/prompts${query}`);
  }
  updateUserPrompt(kind: PromptKind, body: UpdatePromptRequest) {
    return this.req<{ ok: true }>("PUT", `/api/me/prompts/${kind}`, body);
  }
  updateOrganizationPrompt(orgId: string, kind: PromptKind, body: UpdatePromptRequest) {
    return this.req<{ ok: true }>("PUT", `/api/orgs/${orgId}/prompts/${kind}`, body);
  }
  updateRepositoryPrompt(orgId: string, repoId: string, kind: PromptKind, body: UpdatePromptRequest) {
    return this.req<{ ok: true }>("PUT", `/api/orgs/${orgId}/repos/${repoId}/prompts/${kind}`, body);
  }
  createOrg(body: z.infer<typeof CreateOrgRequest>) {
    return this.req<{ org: Org }>("POST", "/api/orgs", body);
  }
  listTeams(orgId: string) {
    return this.req<import("./teams.ts").TeamsResponse>("GET", `/api/orgs/${orgId}/teams`);
  }
  listAutomations(orgId: string) {
    return this.req<import("./automations.ts").AutomationsResponse>("GET", `/api/orgs/${orgId}/automations`);
  }
  automationOptions(orgId: string) {
    return this.req<import("./automations.ts").AutomationOptionsResponse>("GET", `/api/orgs/${orgId}/automations/options`);
  }
  createAutomation(orgId: string, body: z.input<typeof import("./automations.ts").SaveAutomationRequest>) {
    return this.req<import("./automations.ts").AutomationResponse>("POST", `/api/orgs/${orgId}/automations`, body);
  }
  updateAutomation(orgId: string, automationId: string, body: z.input<typeof import("./automations.ts").SaveAutomationRequest>) {
    return this.req<import("./automations.ts").AutomationResponse>("PUT", `/api/orgs/${orgId}/automations/${automationId}`, body);
  }
  setAutomationEnabled(orgId: string, automationId: string, enabled: boolean) {
    return this.req<import("./automations.ts").AutomationResponse>("PATCH", `/api/orgs/${orgId}/automations/${automationId}/enabled`, { enabled });
  }
  deleteAutomation(orgId: string, automationId: string) {
    return this.req<{ ok: true }>("DELETE", `/api/orgs/${orgId}/automations/${automationId}`);
  }
  runAutomation(orgId: string, automationId: string) {
    return this.req<import("./automations.ts").AutomationRunsResponse>("POST", `/api/orgs/${orgId}/automations/${automationId}/run`, {});
  }
  automationRuns(orgId: string, automationId: string) {
    return this.req<import("./automations.ts").AutomationRunsResponse>("GET", `/api/orgs/${orgId}/automations/${automationId}/runs`);
  }
  createTeam(orgId: string, body: import("./teams.ts").SaveTeamRequest) {
    return this.req<import("./teams.ts").TeamResponse>("POST", `/api/orgs/${orgId}/teams`, body);
  }
  updateTeam(orgId: string, teamId: string, body: import("./teams.ts").SaveTeamRequest) {
    return this.req<import("./teams.ts").TeamResponse>("PUT", `/api/orgs/${orgId}/teams/${teamId}`, body);
  }
  deleteTeam(orgId: string, teamId: string) {
    return this.req<{ ok: true }>("DELETE", `/api/orgs/${orgId}/teams/${teamId}`);
  }
  orgDetail(orgId: string) {
    return this.req<z.infer<typeof OrgDetailResponse>>("GET", `/api/orgs/${orgId}`);
  }
  updateOrganizationSettings(orgId: string, body: import("./api.ts").UpdateOrganizationSettingsRequest) {
    return this.req<{ org: Org }>("PUT", `/api/orgs/${orgId}/settings`, body);
  }
  createInvite(orgId: string, body: z.infer<typeof CreateInviteRequest>) {
    return this.req<z.infer<typeof CreateInviteResponse>>("POST", `/api/orgs/${orgId}/invites`, body);
  }
  revokeInvite(orgId: string, inviteId: string) {
    return this.req<void>("DELETE", `/api/orgs/${orgId}/invites/${inviteId}`);
  }
  acceptInvite(body: z.infer<typeof AcceptInviteRequest>) {
    return this.req<{ org: Org }>("POST", "/api/invites/accept", body);
  }

  // repos
  listRepos(orgId: string) {
    return this.req<{ repos: Repo[] }>("GET", `/api/orgs/${orgId}/repos`);
  }
  createRepo(orgId: string, body: z.infer<typeof CreateRepoRequest>) {
    return this.req<{ repo: Repo }>("POST", `/api/orgs/${orgId}/repos`, body);
  }
  updateRepositorySettings(orgId: string, repoId: string, body: import("./api.ts").UpdateRepositorySettingsRequest) {
    return this.req<{ repo: Repo }>("PUT", `/api/orgs/${orgId}/repos/${repoId}/settings`, body);
  }
  /** Persists the caller's project order for this organization and returns the
   * organization's repositories in it. */
  reorderRepos(orgId: string, repoIds: string[]) {
    return this.req<{ repos: Repo[] }>("PUT", `/api/orgs/${orgId}/repos/order`, { repoIds });
  }

  // github app
  githubStatus(orgId: string, client?: "native") {
    const query = client ? `?client=${client}` : "";
    return this.req<z.infer<typeof GitHubStatusResponse>>("GET", `/api/orgs/${orgId}/github${query}`);
  }
  githubRepos(orgId: string) {
    return this.req<z.infer<typeof GitHubReposResponse>>("GET", `/api/orgs/${orgId}/github/repos`);
  }
  integrationStatus(orgId: string, provider: string, client?: "native") {
    const query = client ? `?client=${client}` : "";
    return this.req<z.infer<typeof IntegrationStatusResponse>>(
      "GET",
      `/api/orgs/${orgId}/integrations/${encodeURIComponent(provider)}${query}`,
    );
  }
  updateIntegration(orgId: string, provider: string, body: z.infer<typeof UpdateIntegrationRequest>) {
    return this.req<z.infer<typeof IntegrationStatusResponse>>(
      "PUT",
      `/api/orgs/${orgId}/integrations/${encodeURIComponent(provider)}`,
      body,
    );
  }
  disconnectIntegration(orgId: string, provider: string) {
    return this.req<void>("DELETE", `/api/orgs/${orgId}/integrations/${encodeURIComponent(provider)}`);
  }
  /** What an account-link challenge would bind. Never consumes it. */
  accountLinkPreview(token: string) {
    return this.req<z.infer<typeof AccountLinkPreviewResponse>>(
      "GET",
      `/api/account-links/${encodeURIComponent(token)}`,
    );
  }
  /** Consumes the challenge after the person confirmed the preview. */
  redeemAccountLink(body: z.infer<typeof RedeemAccountLinkRequest>) {
    return this.req<z.infer<typeof RedeemAccountLinkResponse>>("POST", "/api/account-links", body);
  }
  /** Worker-authed: short-lived git credentials for a repo. */
  workerGitCredentials(repoId: string) {
    return this.req<z.infer<typeof GitCredentialsResponse>>(
      "GET",
      `/api/worker/repos/${repoId}/git-credentials`,
    );
  }
  /** Worker-authed: records the latest git-native handoff backup this worker
   * pushed for a worktree, so a later cross-worker reroute can fetch it. */
  recordHandoffBackup(worktreeId: string, body: z.infer<typeof RecordHandoffBackupRequest>, signal?: AbortSignal) {
    return this.req<void>("PUT", `/api/worker/worktrees/${worktreeId}/handoff-backup`, body, signal);
  }

  // workers
  listWorkers(orgId: string) {
    return this.req<import("./api.ts").WorkersResponse>("GET", `/api/orgs/${orgId}/workers`);
  }
  /** Owner only: replaces who besides the owner may use the worker. */
  updateWorkerAccess(orgId: string, workerId: string, body: import("./api.ts").UpdateWorkerAccessRequest) {
    return this.req<import("./api.ts").WorkerResponse>("PUT", `/api/orgs/${orgId}/workers/${workerId}/access`, body);
  }
  /** Owner only: hands the worker to another member. */
  transferWorker(orgId: string, workerId: string, body: import("./api.ts").TransferWorkerRequest) {
    return this.req<import("./api.ts").WorkerResponse>("PUT", `/api/orgs/${orgId}/workers/${workerId}/owner`, body);
  }
  homeStats(orgId: string) {
    return this.req<import("./api.ts").HomeStatsResponse>("GET", `/api/orgs/${orgId}/home-stats`);
  }
  registerWorker(orgId: string, body: z.infer<typeof RegisterWorkerRequest>) {
    return this.req<z.infer<typeof RegisterWorkerResponse>>(
      "POST",
      `/api/orgs/${orgId}/workers`,
      body,
    );
  }

  /**
   * One-time activation call. It is authenticated by both the activation
   * bearer token and a proof made by the key being enrolled; every later
   * request for this worker needs a proof from that key.
   */
  enrollWorkerProof(publicKey: WorkerPublicJwk) {
    return this.req<{ enrolled: true; thumbprint: string }>(
      "POST",
      "/api/worker/auth/enroll",
      { publicKey },
    );
  }
  /** Asks an online worker to re-read its provider subscriptions. The answer
   * arrives as a `worker.status` update, not in this response. */
  refreshWorkerUsage(orgId: string, workerId: string) {
    return this.req<void>("POST", `/api/orgs/${orgId}/workers/${workerId}/usage/refresh`);
  }
  deleteWorker(orgId: string, workerId: string) {
    return this.req<void>("DELETE", `/api/orgs/${orgId}/workers/${workerId}`);
  }

  /** Issues a short-lived, one-use grant for an optional direct data path.
   * Authorization and resource ownership are still decided by the API. */
  createDirectTicket(body: DirectTicketRequest) {
    return this.req<DirectTicketResponse>("POST", "/api/direct/tickets", body);
  }

  /** A one-use ticket for a LocalApi session on the worker's loopback
   * listener (K4). The browser hands it to the daemon in `local.hello`. */
  createLocalSession(body: LocalSessionTicketRequest) {
    return this.req<LocalSessionTicketResponse>("POST", "/api/direct/local-session", body);
  }

  /** Best-effort revocation for a ticket that has not been consumed. */
  revokeDirectTicket(ticketId: string) {
    return this.req<void>("DELETE", `/api/direct/tickets/${encodeURIComponent(ticketId)}`);
  }

  /** Native proof identities bound to this account. Browser settings can list
   * and revoke them with its cookie session; no device proof is required for
   * the browser itself. */
  listNativeDevices() {
    return this.req<z.infer<typeof NativeDevicesResponse>>("GET", "/api/native/devices");
  }

  /** Signs the device out: its bound session ends and its sockets close. */
  revokeNativeDevice(deviceId: string) {
    return this.req<void>("DELETE", `/api/native/devices/${encodeURIComponent(deviceId)}`);
  }

  /** The account's sign-in sessions, by id and metadata only. */
  listSessions() {
    return this.req<z.infer<typeof AuthSessionsResponse>>("GET", "/api/me/sessions");
  }

  revokeSession(sessionId: string) {
    return this.req<void>("DELETE", `/api/me/sessions/${encodeURIComponent(sessionId)}`);
  }

  /** Signs out everywhere but here. */
  revokeOtherSessions() {
    return this.req<z.infer<typeof RevokeOtherSessionsResponse>>("POST", "/api/me/sessions/revoke-others");
  }

  // worktrees
  listWorktrees(repoId: string) {
    return this.req<{ worktrees: Worktree[] }>("GET", `/api/repos/${repoId}/worktrees`);
  }
  listRepoBranches(repoId: string) {
    return this.req<z.infer<typeof RepoBranchesResponse>>("GET", `/api/repos/${repoId}/branches`);
  }
  listRepoPullRequests(repoId: string) {
    return this.req<z.infer<typeof RepoPullRequestsResponse>>("GET", `/api/repos/${repoId}/pull-requests`);
  }
  /** Open Linear issues for the task picker; `query` searches Linear's own content. */
  listLinearIssues(orgId: string, query?: string) {
    const search = query?.trim() ? `?query=${encodeURIComponent(query.trim())}` : "";
    return this.req<z.infer<typeof LinearIssuesResponse>>("GET", `/api/orgs/${orgId}/linear/issues${search}`);
  }

  /** A normalized repository icon, for connected repositories and picker rows
   * alike — both carry the same content address. Browser clients can use this
   * as an image URL (their session cookie is first-party); native clients use
   * their authenticated binary request helper. The response is immutable, so
   * it is fetched at most once per client. */
  repoIconUrl(orgId: string, iconId: string) {
    return `${this.opts.baseUrl}/api/orgs/${orgId}/repo-icons/${iconId}`;
  }
  createTask(repoId: string, body: z.input<typeof CreateTaskRequest>) {
    return this.req<z.infer<typeof CreateTaskResponse>>("POST", `/api/repos/${repoId}/tasks`, body);
  }
  worktreeDetail(worktreeId: string) {
    return this.req<z.infer<typeof WorktreeDetailResponse>>("GET", `/api/worktrees/${worktreeId}`);
  }
  archiveWorktree(worktreeId: string) {
    return this.req<import("./api.ts").WorktreeMutationResponse>("POST", `/api/worktrees/${worktreeId}/archive`);
  }
  restoreWorktree(worktreeId: string) {
    return this.req<import("./api.ts").WorktreeMutationResponse>("POST", `/api/worktrees/${worktreeId}/restore`);
  }
  /** Renames a task. The server broadcasts `worktree.updated`. */
  updateWorktree(worktreeId: string, body: import("./api.ts").UpdateWorktreeRequest) {
    return this.req<import("./api.ts").UpdateWorktreeResponse>("PATCH", `/api/worktrees/${worktreeId}`, body);
  }
  /** Throws away uncommitted changes to the named paths in the worker's checkout. */
  discardChanges(worktreeId: string, body: import("./api.ts").DiscardChangesRequest) {
    return this.req<import("./api.ts").DiscardChangesResponse>("POST", `/api/worktrees/${worktreeId}/discard`, body, undefined, WORKER_ACTION_TIMEOUT_MS);
  }
  /** A signed no-op. Its real use is the clock: a stale proof's answer
   * corrects the offset later proofs, the worker socket's included, are
   * stamped with. */
  workerClock() {
    return this.req<{ serverTime: number }>("GET", "/api/worker/clock");
  }
  listPurgeableWorktrees() {
    return this.req<{ worktrees: import("./api.ts").WorktreePurgeCandidate[] }>("GET", "/api/worker/worktrees/purgeable");
  }
  claimWorktreePurge(worktreeId: string) {
    return this.req<import("./api.ts").WorktreePurgeClaimResponse>("POST", `/api/worker/worktrees/${worktreeId}/purge-claim`);
  }
  completeWorktreePurge(worktreeId: string, claimToken: string) {
    return this.req<void>("DELETE", `/api/worker/worktrees/${worktreeId}/purge`, { claimToken });
  }
  /** The latest snapshot without diff text or the path list (K3), or null
   * before the worker has uploaded one. Clients never fetch the full blob. */
  worktreeSnapshotSummary(worktreeId: string) {
    return this.req<SnapshotSummary | null>("GET", `/api/worktrees/${worktreeId}/snapshot?view=${SNAPSHOT_SUMMARY_VIEW}`);
  }
  /** One file's diff in one snapshot. Immutable per `(snapshotId, path,
   * scope)`; a pruned snapshot answers 410 `snapshot_gone`, and the caller
   * reloads the summary. */
  snapshotDiff(snapshotId: string, path: string, scope: SnapshotDiffScope) {
    return this.req<SnapshotDiffResponse>(
      "GET",
      `/api/snapshots/${encodeURIComponent(snapshotId)}/diff?path=${encodeURIComponent(path)}&scope=${scope}`,
      undefined,
      undefined,
      "transfer",
    );
  }
  /** Every path in a snapshot, for resolving transcript file links. Loaded
   * lazily: only a click on a link needs it. */
  snapshotPaths(snapshotId: string) {
    return this.req<SnapshotPathsResponse>("GET", `/api/snapshots/${encodeURIComponent(snapshotId)}/paths`, undefined, undefined, "transfer");
  }
  worktreeFile(worktreeId: string, path: string) {
    return this.req<z.infer<typeof FileContentResponse>>(
      "GET",
      `/api/worktrees/${worktreeId}/file?path=${encodeURIComponent(path)}`,
    );
  }
  /** Saves an edited text file in the worker's checkout (409 `file_conflict`
   * when it changed since `expectedHash`). */
  writeWorktreeFile(worktreeId: string, body: import("./api.ts").WriteFileRequest) {
    return this.req<import("./api.ts").WriteFileResponse>("PUT", `/api/worktrees/${worktreeId}/file`, body, undefined, WORKER_ACTION_TIMEOUT_MS);
  }
  /** One directory's children, live from the worker's checkout. `path` is
   * empty for the worktree root. */
  worktreeDirectory(worktreeId: string, path: string) {
    return this.req<import("./api.ts").DirectoryListingResponse>(
      "GET",
      `/api/worktrees/${worktreeId}/dir?path=${encodeURIComponent(path)}`,
    );
  }
  prWorkspace(worktreeId: string, refresh = false) {
    return this.req<PrWorkspace>("GET", `/api/worktrees/${worktreeId}/pr?refresh=${refresh}`, undefined, undefined, WORKER_ACTION_TIMEOUT_MS);
  }
  savePrDraft(worktreeId: string, body: SavePrDraftRequest) {
    return this.req<PrDraft>("PUT", `/api/worktrees/${worktreeId}/pr/draft`, body);
  }
  preparePrWorkspace(worktreeId: string, body: PreparePrRequest) {
    return this.req<PreparePrResponse>("POST", `/api/worktrees/${worktreeId}/pr/prepare`, body, undefined, WORKER_ACTION_TIMEOUT_MS);
  }
  runPrAction(worktreeId: string, body: RunPrActionRequest) {
    return this.req<PrOperation>("POST", `/api/worktrees/${worktreeId}/pr/action`, body, undefined, WORKER_ACTION_TIMEOUT_MS);
  }
  updatePrMetadata(worktreeId: string, body: UpdatePrMetadataRequest) {
    return this.req<PrGitHub>("PATCH", `/api/worktrees/${worktreeId}/pr`, body, undefined, WORKER_ACTION_TIMEOUT_MS);
  }
  pullRequestStatus(worktreeId: string) {
    return this.req<z.infer<typeof PullRequestStatus>>("GET", `/api/worktrees/${worktreeId}/pull-request/status`);
  }
  mergePullRequest(worktreeId: string, body: z.infer<typeof MergePullRequestRequest>) {
    return this.req<z.infer<typeof MergePullRequestResponse>>(
      "POST",
      `/api/worktrees/${worktreeId}/pull-request/merge`,
      body,
      undefined,
      WORKER_ACTION_TIMEOUT_MS,
    );
  }

  // api tokens
  /** Personal MCP/API tokens for this member. Assistant-minted tokens are
   * never listed here. */
  listApiTokens(orgId: string) {
    return this.req<z.infer<typeof ApiTokensResponse>>("GET", `/api/orgs/${orgId}/tokens`);
  }
  /** The plaintext secret is returned exactly once, in this response. */
  createApiToken(orgId: string, body: z.input<typeof CreateApiTokenRequest>) {
    return this.req<z.infer<typeof CreateApiTokenResponse>>("POST", `/api/orgs/${orgId}/tokens`, body);
  }
  revokeApiToken(orgId: string, tokenId: string) {
    return this.req<void>("DELETE", `/api/orgs/${orgId}/tokens/${encodeURIComponent(tokenId)}`);
  }

  // assistant
  /** The member's assistant workspace and its conversations. Renders as an
   * empty, explained state when no capable worker or repository exists. */
  assistantState(orgId: string) {
    return this.req<z.infer<typeof AssistantStateResponse>>("GET", `/api/orgs/${orgId}/assistant`);
  }
  startAssistantConversation(orgId: string, body: z.infer<typeof StartAssistantConversationRequest>) {
    return this.req<z.infer<typeof StartAssistantConversationResponse>>(
      "POST",
      `/api/orgs/${orgId}/assistant/conversations`,
      body,
      undefined,
      ASSISTANT_TIMEOUT_MS,
    );
  }
  archiveAssistantConversation(orgId: string, chatId: string) {
    return this.req<void>("DELETE", `/api/orgs/${orgId}/assistant/conversations/${encodeURIComponent(chatId)}`);
  }
  /** Retires the assistant workspace and provisions a fresh one. */
  resetAssistant(orgId: string) {
    return this.req<z.infer<typeof AssistantStateResponse>>("POST", `/api/orgs/${orgId}/assistant/reset`, undefined, undefined, ASSISTANT_TIMEOUT_MS);
  }

  // channels (Slack, Teams)
  listChannelInstallations(orgId: string) {
    return this.req<z.infer<typeof ChannelInstallationsResponse>>("GET", `/api/orgs/${orgId}/channels`);
  }
  /** Owner-only. Returns the provider consent URL to redirect the browser to. */
  startChannelInstall(orgId: string, provider: string, body?: z.infer<typeof StartChannelInstallRequest>) {
    return this.req<z.infer<typeof StartChannelInstallResponse>>(
      "POST",
      `/api/orgs/${orgId}/channels/${encodeURIComponent(provider)}/install`,
      body ?? {},
    );
  }
  uninstallChannel(orgId: string, provider: string) {
    return this.req<void>("DELETE", `/api/orgs/${orgId}/channels/${encodeURIComponent(provider)}`);
  }
  /** Whether this member's account is bound to their identity in the channel. */
  channelIdentityLink(orgId: string, provider: string) {
    return this.req<z.infer<typeof ChannelIdentityLinkResponse>>(
      "GET",
      `/api/orgs/${orgId}/channels/${encodeURIComponent(provider)}/identity`,
    );
  }

  // chats
  createChat(worktreeId: string, body: z.infer<typeof CreateChatRequest>) {
    return this.req<{ chat: Chat; job: Job }>("POST", `/api/worktrees/${worktreeId}/chats`, body);
  }
  updateChat(chatId: string, body: z.infer<typeof UpdateChatRequest>) {
    return this.req<z.infer<typeof UpdateChatResponse>>("PATCH", `/api/chats/${chatId}`, body);
  }
  handoffChat(chatId: string, body: z.infer<typeof HandoffChatRequest>) {
    return this.req<z.infer<typeof HandoffChatResponse>>("POST", `/api/chats/${chatId}/handoff`, body);
  }
  chatDetail(chatId: string) {
    return this.req<z.infer<typeof ChatDetailResponse>>("GET", `/api/chats/${chatId}`);
  }
  /** One history page (K2). Continue from `coverage`, never from the
   * number of events returned: elided and absent seqs are inside it. */
  chatEvents(chatId: string, query: ChatEventsQuery = { mode: "after", afterSeq: 0, limit: CHAT_EVENTS_DEFAULT_LIMIT }) {
    return this.req<ChatEventsPage>("GET", `/api/chats/${chatId}/events?${formatChatEventsQuery(query)}`);
  }
  /**
   * `chatId` and `reprovisioned` are only present for assistant conversations,
   * and only the API fills them in: when a lost worker forces a fresh workspace
   * the reply names the *replacement* chat, so a client can open and subscribe
   * to it instead of falling back to re-listing. Both are absent for any other
   * chat.
   */
  sendMessage(chatId: string, body: z.infer<typeof SendMessageRequest>) {
    return this.req<{ ok: true; chatId?: string; reprovisioned?: boolean }>(
      "POST",
      `/api/chats/${chatId}/messages`,
      body,
    );
  }
  /** Rewrites a message still queued behind a working agent. Resolving means
   * the edit was accepted for delivery, not that the provider had not already
   * taken the turn — the worker reports that race on the chat itself. */
  reviseMessage(chatId: string, turnId: string, body: z.infer<typeof ReviseMessageRequest>) {
    return this.req<{ ok: true }>(
      "PATCH",
      `/api/chats/${chatId}/turns/${turnId}/message`,
      body,
    );
  }
  /** Deletes a message still queued behind a working agent. As with an edit,
   * the worker reports on the chat if the provider had already taken it. */
  withdrawMessage(chatId: string, turnId: string) {
    return this.req<{ ok: true }>("DELETE", `/api/chats/${chatId}/turns/${turnId}/message`);
  }
  /** Sends a queued message now: it jumps the queue and the agent's current
   * turn is interrupted so it can start. */
  sendMessageNow(chatId: string, turnId: string) {
    return this.req<{ ok: true }>("POST", `/api/chats/${chatId}/turns/${turnId}/send-now`);
  }
  /** Cancels one run. `jobId` names it, so a Stop can never land on a run
   * someone started after the press; a run that already ended is a no-op.
   * The same id goes to the LocalApi cancel hint. */
  cancelChat(chatId: string, body: CancelChatRequest) {
    return this.req<{ ok: true }>("POST", `/api/chats/${chatId}/cancel`, body);
  }

  // attachments
  /** Uploads one file and returns its durable ref. Bypasses `req()`: the body
   * is raw bytes and the media type travels as the request's content-type. */
  async uploadAttachment(
    orgId: string,
    /** `Uint8Array` is pinned to a non-shared buffer because that is what the
     * request body types accept; `Blob`/`File` is the browser's natural form. */
    bytes: Uint8Array<ArrayBuffer> | Blob,
    mediaType: AttachmentMediaType,
    name?: string,
    dimensions?: { width: number; height: number },
  ): Promise<AttachmentRef> {
    const query = new URLSearchParams();
    if (name) query.set("name", name);
    if (dimensions) {
      query.set("width", String(dimensions.width));
      query.set("height", String(dimensions.height));
    }
    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return this.send("POST", `${this.opts.baseUrl}/api/orgs/${orgId}/attachments${suffix}`, {
      headers: { "content-type": mediaType },
      // Raw bytes are wrapped rather than passed through: `Uint8Array` stopped
      // structurally matching `BufferSource` once typed arrays became generic
      // over their buffer, and the request is identical either way.
      body: bytes instanceof Blob ? bytes : new Blob([bytes], { type: mediaType }),
      deadline: "transfer",
    }, async (res) => ((await res.json()) as z.infer<typeof UploadAttachmentResponse>).attachment);
  }
  /** URL for rendering or downloading an uploaded file. Usable directly in
   * cookie-session clients; token clients must use `downloadAttachment`. */
  attachmentUrl(orgId: string, attachmentId: string): string {
    return `${this.opts.baseUrl}/api/orgs/${orgId}/attachments/${attachmentId}`;
  }
  /** Fetches file bytes with the client's auth applied, for bearer-token
   * clients (native/CLI) that cannot put credentials on a URL. */
  downloadAttachment(orgId: string, attachmentId: string): Promise<Blob> {
    return this.send("GET", this.attachmentUrl(orgId, attachmentId), { deadline: "transfer" }, (res) => res.blob());
  }

  // push notifications
  registerDevice(body: z.infer<typeof RegisterDeviceRequest>) {
    return this.req<{ registered: true }>("POST", "/api/push/devices", body);
  }
  unregisterDevice(deviceToken: string) {
    return this.req<void>("DELETE", `/api/push/devices/${encodeURIComponent(deviceToken)}`);
  }

  // worker-facing
  uploadSnapshot(worktreeId: string, body: z.infer<typeof UploadSnapshotRequest>) {
    return this.req<{ snapshotId: string }>(
      "PUT",
      `/api/worker/worktrees/${worktreeId}/snapshot`,
      body,
      undefined,
      "transfer",
    );
  }

  /** wss URL for the org client socket. */
  orgSocketUrl(orgId: string): string {
    return withProtocolVersion(`${this.opts.baseUrl.replace(/^http/, "ws")}/api/ws/org/${orgId}`, "web");
  }
  /** wss URL for the worker socket (token goes in the `authorization` header only — never the URL). */
  workerSocketUrl(): string {
    return withProtocolVersion(`${this.opts.baseUrl.replace(/^http/, "ws")}/api/ws/worker`, "worker");
  }

  /** Headers for the worker WebSocket upgrade; callers must create a fresh
   * set for every reconnect because every proof has a one-use replay id. */
  async workerSocketHeaders(): Promise<Record<string, string>> {
    return this.authorizationHeaders("GET", this.workerSocketUrl());
  }
}
